import React, { useEffect, useState } from 'react';
import Parse from '../../parseConfig';
import { createBuiltHomeAcl, getBuiltHomeAccessRoles } from './builtHomeAccess';
import {
  BUILT_HOME_REVIEW_FILTERS,
  BUILT_HOME_SORTS,
  BUILT_HOME_TYPES,
  BUILD_STYLES,
  formatCoordinates,
  normalizeAddress,
  parseCoordinates,
  parseDateInput,
  sortBuiltHomes,
} from './builtProjectUtils';
import { sanitizeImageFile } from './imageSanitizer';
import { fetchSalesConsultants } from './fetchSalesConsultants';
import BuiltCoordinatePicker, { MapPinIcon } from './BuiltCoordinatePicker';
import BuiltAdminDropdown from './BuiltAdminDropdown';
import useBuiltModalEscape from './useBuiltModalEscape';
import {
  CUSTOM_BASE_PLAN_VALUE,
  getBasePlanDraftUpdate,
  getBasePlanOptions,
  getBasePlanSelectionValue,
  saveBasePlanSelection,
} from './basePlanSelection';
import '../styles/StockListingAdminModal.css';
import '../styles/phcolours.css';
import TrashIcon from '../../images/trash.svg';

const BuiltHome = Parse.Object.extend('BuiltHome');
const Plan = Parse.Object.extend('Plan');
const MANUAL_CONSULTANT_VALUE = '__manual__';

const UploadIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" focusable="false">
    <path d="M10 3.5 5.8 7.7l1.1 1.1 2.3-2.3v7h1.6v-7l2.3 2.3 1.1-1.1L10 3.5Z" />
    <path d="M4.5 15h11v1.5h-11V15Z" />
  </svg>
);

const createEmptyDraft = () => ({
  id: null,
  address: '',
  clientName: '',
  basePlanId: '',
  basePlanIsCustom: false,
  salesConsultantId: '',
  salesConsultantManualName: '',
  isManualSalesConsultant: false,
  type: '',
  buildStyle: BUILD_STYLES[0],
  completedYear: '',
  consentedDate: '',
  needsReview: 0,
  coordinates: '',
  photoName: '',
  photoUploadFile: null,
  removePhoto: false,
  _raw: null,
});

const geocodeAddress = async (address) => {
  if (!window.google?.maps?.Geocoder) {
    throw new Error('The map is still loading. Please wait a moment and save again.');
  }

  const geocoder = new window.google.maps.Geocoder();
  const response = await geocoder.geocode({ address });
  const location = response.results?.[0]?.geometry?.location;

  if (!location) {
    throw new Error('That address could not be located. Check the address or enter coordinates instead.');
  }

  return { latitude: location.lat(), longitude: location.lng() };
};

const setOptionalString = (object, field, value) => {
  const trimmed = String(value || '').trim();
  if (trimmed) object.set(field, trimmed);
  else object.unset(field);
};

export default function BuiltHomeAdminModal({
  isOpen,
  homes = [],
  basePlans = [],
  isLoadingBasePlans = false,
  basePlanLoadError = '',
  onEditHome = () => {},
  onLocateHome = () => {},
  onOpenReview = () => {},
  onClose = () => {},
  onHomesChanged = async () => {},
}) {
  const [activeDraft, setActiveDraft] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSanitizingPhoto, setIsSanitizingPhoto] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState(BUILT_HOME_SORTS.LATEST_LISTED);
  const [reviewFilter, setReviewFilter] = useState(BUILT_HOME_REVIEW_FILTERS.ALL);
  const [salesConsultants, setSalesConsultants] = useState([]);
  const [isLoadingConsultants, setIsLoadingConsultants] = useState(false);
  const [consultantLoadError, setConsultantLoadError] = useState('');
  const [isCoordinatePickerOpen, setIsCoordinatePickerOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setActiveDraft(null);
      setIsSaving(false);
      setIsSanitizingPhoto(false);
      setSaveError('');
      setSearchQuery('');
      setSortOrder(BUILT_HOME_SORTS.LATEST_LISTED);
      setReviewFilter(BUILT_HOME_REVIEW_FILTERS.ALL);
      setSalesConsultants([]);
      setIsLoadingConsultants(false);
      setConsultantLoadError('');
      setIsCoordinatePickerOpen(false);
      return;
    }

  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let isCancelled = false;
    setIsLoadingConsultants(true);
    setConsultantLoadError('');

    fetchSalesConsultants()
      .then((consultants) => {
        if (!isCancelled) setSalesConsultants(consultants);
      })
      .catch((error) => {
        console.error('Failed to load sales consultants:', error);
        if (!isCancelled) {
          setSalesConsultants([]);
          setConsultantLoadError('Sales consultants could not be loaded.');
        }
      })
      .finally(() => {
        if (!isCancelled) setIsLoadingConsultants(false);
      });

    return () => { isCancelled = true; };
  }, [isOpen]);

  useBuiltModalEscape(isOpen, () => {
    if (activeDraft) {
      setActiveDraft(null);
      setSaveError('');
    } else {
      onClose();
    }
  });

  if (!isOpen) return null;

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const searchedHomes = normalizedSearchQuery
    ? homes.filter((home) =>
        [home.address, home.clientName, home.salesConsultantName]
          .some((value) => String(value || '').toLowerCase().includes(normalizedSearchQuery))
      )
    : homes;
  const reviewFilteredHomes = searchedHomes.filter((home) => {
    if (reviewFilter === BUILT_HOME_REVIEW_FILTERS.NEEDS_REVIEW) return home.needsReview === 1;
    if (reviewFilter === BUILT_HOME_REVIEW_FILTERS.REVIEWED) return home.needsReview !== 1;
    return true;
  });
  const filteredHomes = sortBuiltHomes(reviewFilteredHomes, sortOrder);
  const reviewCount = homes.filter((home) => home.needsReview === 1).length;

  const updateDraftField = (field, value) => {
    setActiveDraft((current) => ({ ...current, [field]: value }));
  };

  const closeEditor = () => {
    if (isSaving || isSanitizingPhoto) return;
    setActiveDraft(null);
    setSaveError('');
  };

  const getHomeObjectForSave = async () => {
    const homeObject = new BuiltHome();
    homeObject.setACL(createBuiltHomeAcl(await getBuiltHomeAccessRoles()));
    return homeObject;
  };

  const handleSave = async () => {
    if (!activeDraft || isSanitizingPhoto) return;

    const address = activeDraft.address.trim();

    let manualCoordinates;
    try {
      manualCoordinates = parseCoordinates(activeDraft.coordinates);
    } catch (error) {
      setSaveError(error.message);
      return;
    }

    if (!address && !manualCoordinates) {
      setSaveError('Enter an address or coordinates so the home can be plotted.');
      return;
    }

    if (!BUILD_STYLES.includes(activeDraft.buildStyle)) {
      setSaveError('Choose Solid Timber or Timber Framed.');
      return;
    }

    const completedYear = activeDraft.completedYear === '' ? null : Number(activeDraft.completedYear);
    const currentYear = new Date().getFullYear();
    if (completedYear !== null && (!Number.isInteger(completedYear) || completedYear < 1800 || completedYear > currentYear)) {
      setSaveError(`Year built must be between 1800 and ${currentYear}.`);
      return;
    }

    let consentedDate;
    try {
      consentedDate = parseDateInput(activeDraft.consentedDate);
    } catch (error) {
      setSaveError(error.message);
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      const homeObject = await getHomeObjectForSave();
      // These fields were part of the first draft only. Unset them on any
      // existing object so they are no longer retained in Parse.
      homeObject.unset('displayTitle');
      homeObject.unset('inactive');
      setOptionalString(homeObject, 'address', address);
      setOptionalString(homeObject, 'addressKey', normalizeAddress(address));
      setOptionalString(homeObject, 'clientName', activeDraft.clientName);
      setOptionalString(homeObject, 'buildStyle', activeDraft.buildStyle);
      setOptionalString(homeObject, 'type', activeDraft.type);
      saveBasePlanSelection(homeObject, activeDraft, (planId) => Plan.createWithoutData(planId));

      if (consentedDate) homeObject.set('consentedDate', consentedDate);
      else homeObject.unset('consentedDate');

      if (activeDraft.salesConsultantId) {
        homeObject.set('salesConsultant', Parse.User.createWithoutData(activeDraft.salesConsultantId));
        homeObject.unset('salesConsultantName');
      } else {
        homeObject.unset('salesConsultant');
        setOptionalString(homeObject, 'salesConsultantName', activeDraft.salesConsultantManualName);
      }

      if (completedYear === null) homeObject.unset('completedYear');
      else homeObject.set('completedYear', completedYear);

      if (manualCoordinates) {
        homeObject.set('geocodedCoordinates', new Parse.GeoPoint(manualCoordinates));
      } else if (address) {
        const savedAddress = homeObject.get('address');
        homeObject.set('geocodedCoordinates', new Parse.GeoPoint(await geocodeAddress(savedAddress)));
      } else {
        homeObject.unset('geocodedCoordinates');
      }

      if (activeDraft.photoUploadFile) {
        homeObject.set('photo', new Parse.File(activeDraft.photoUploadFile.name, activeDraft.photoUploadFile));
      } else if (activeDraft.removePhoto) {
        homeObject.unset('photo');
      }

      await homeObject.save();

      try {
        await onHomesChanged();
      } catch (refreshError) {
        console.error('Built home saved but the list failed to refresh:', refreshError);
      }

      setActiveDraft(null);
    } catch (error) {
      console.error('Failed to save built home:', error);
      setSaveError(
        error?.message === 'Object not found.'
          ? 'This home could not be found when saving. Close the editor, refresh the list, and try again.'
          : error?.message || 'Failed to save home.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="stock-admin-overlay built-admin-overlay" onClick={onClose}>
      <div className="stock-admin-panel" onClick={(event) => event.stopPropagation()}>
          <div className="stock-admin-header">
            <div className="stock-admin-header-top-row">
              <div className="stock-admin-title">Admin</div>
              <div className="stock-admin-header-actions">
                <button
                  type="button"
                  className="stock-admin-secondary-button built-admin-review-button"
                  onClick={onOpenReview}
                  disabled={reviewCount === 0}
                >
                  Review ({reviewCount})
                </button>
                <button
                  type="button"
                  className="stock-admin-secondary-button stock-admin-new-button built-admin-new-button"
                  onClick={() => {
                    setActiveDraft(createEmptyDraft());
                    setSaveError('');
                  }}
                >
                  New Home
                </button>
                <button type="button" className="stock-admin-close-button built-admin-close-button" onClick={onClose} aria-label="Close built homes admin">
                  ×
                </button>
              </div>
            </div>

            <div className="stock-admin-header-main">
              <input
                type="text"
                className="stock-admin-search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search homes"
              />
              <div className="stock-admin-controls-row">
                <div className="built-admin-sort-dropdown">
                  <BuiltAdminDropdown
                    ariaLabel="Sort homes"
                    value={sortOrder}
                    onChange={setSortOrder}
                    options={[
                      { value: BUILT_HOME_SORTS.LATEST_LISTED, label: 'Latest Listed' },
                      { value: BUILT_HOME_SORTS.OLDEST_LISTED, label: 'Oldest Listed' },
                      { value: BUILT_HOME_SORTS.LATEST_BUILT, label: 'Latest Built' },
                      { value: BUILT_HOME_SORTS.OLDEST_BUILT, label: 'Oldest Built' },
                    ]}
                  />
                </div>
                <div className="built-admin-review-segments" role="group" aria-label="Review status">
                  {[
                    [BUILT_HOME_REVIEW_FILTERS.ALL, 'All'],
                    [BUILT_HOME_REVIEW_FILTERS.REVIEWED, 'Reviewed'],
                    [BUILT_HOME_REVIEW_FILTERS.NEEDS_REVIEW, 'Unreviewed'],
                  ].map(([value, label]) => (
                    <button
                      type="button"
                      key={value}
                      className={`built-admin-review-segment${reviewFilter === value ? ' active' : ''}`}
                      aria-pressed={reviewFilter === value}
                      onClick={() => setReviewFilter(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="stock-admin-list-count">{filteredHomes.length} {filteredHomes.length === 1 ? 'home' : 'homes'}</div>
              </div>
            </div>
          </div>

          <div className="stock-admin-list">
            {filteredHomes.map((home) => (
              <div
                key={home.id}
                className="stock-admin-row built-admin-row"
              >
                <button
                  type="button"
                  className="stock-admin-row-edit-button"
                  onClick={() => onEditHome(home)}
                  aria-label={`Edit ${home.address || home.clientName || 'home'}`}
                >
                  <div className="stock-admin-row-copy">
                    <div className="stock-admin-row-title">{home.address || 'Coordinates only'}</div>
                    {home.clientName && <div className="stock-admin-row-location">{home.clientName}</div>}
                  </div>
                  <div className="stock-admin-row-meta">
                    {home.needsReview === 1 && <span className="stock-admin-status-pill built-review-status-pill">Needs Review</span>}
                    <span className="stock-admin-status-pill">{home.buildStyle || 'Draft'}</span>
                  </div>
                </button>
                {home.coordinates && (
                  <button
                    type="button"
                    className="built-admin-map-home-button"
                    onClick={() => onLocateHome(home)}
                    aria-label={`Show ${home.address || home.clientName || 'home'} on map`}
                    title="Show on map"
                  >
                    <span className="built-admin-map-home-icon" aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
            {homes.length === 0 && <div className="stock-admin-empty">No built homes have been added yet.</div>}
            {homes.length > 0 && filteredHomes.length === 0 && <div className="stock-admin-empty">No homes match that search.</div>}
          </div>
        </div>

      {activeDraft && (
        <div className="stock-admin-editor-panel" onClick={(event) => event.stopPropagation()}>
          <div className="stock-admin-header stock-admin-editor-header">
            <div className="stock-admin-title">New Home</div>
          </div>

          <div className="stock-admin-form">
            <label className="stock-admin-field stock-admin-field-full">
              <span>Address</span>
              <input
                type="text"
                value={activeDraft.address}
                onChange={(event) => updateDraftField('address', event.target.value)}
                placeholder="Used to place the marker when coordinates are blank"
              />
            </label>

            <div className="stock-admin-field stock-admin-field-full">
              <span>Coordinates</span>
              <div className="built-coordinate-input-row">
                <input
                  type="text"
                  value={activeDraft.coordinates}
                  onChange={(event) => updateDraftField('coordinates', event.target.value)}
                  placeholder="-43.5321,172.6362 (takes precedence over address)"
                />
                <button type="button" className="built-coordinate-map-button" onClick={() => setIsCoordinatePickerOpen(true)} aria-label="Choose coordinates on map" title="Choose coordinates on map"><MapPinIcon /></button>
              </div>
            </div>

            <label className="stock-admin-field">
              <span>Client Name</span>
              <input type="text" value={activeDraft.clientName} onChange={(event) => updateDraftField('clientName', event.target.value)} />
            </label>

            <div className="stock-admin-field">
              <span>Base Plan</span>
              <BuiltAdminDropdown
                ariaLabel="Base Plan"
                value={getBasePlanSelectionValue(activeDraft)}
                onChange={(value) => setActiveDraft((current) => ({ ...current, ...getBasePlanDraftUpdate(value) }))}
                disabled={isLoadingBasePlans}
                disabledLabel="Loading plans…"
                placeholder="Not assigned"
                options={getBasePlanOptions(basePlans)}
                searchable
                searchPlaceholder="Search plans…"
                persistentOptionValues={[CUSTOM_BASE_PLAN_VALUE, '']}
              />
              {basePlanLoadError && <span className="stock-admin-field-error">{basePlanLoadError}</span>}
            </div>

            <div className="stock-admin-field">
              <span>Sales Consultant</span>
              <BuiltAdminDropdown
                ariaLabel="Sales Consultant"
                value={activeDraft.isManualSalesConsultant ? MANUAL_CONSULTANT_VALUE : activeDraft.salesConsultantId}
                onChange={(value) => setActiveDraft((current) => ({
                  ...current,
                  salesConsultantId: value === MANUAL_CONSULTANT_VALUE ? '' : value,
                  salesConsultantManualName: value === MANUAL_CONSULTANT_VALUE ? current.salesConsultantManualName : '',
                  isManualSalesConsultant: value === MANUAL_CONSULTANT_VALUE,
                }))}
                disabled={isLoadingConsultants}
                disabledLabel="Loading consultants…"
                placeholder="Not assigned"
                options={[
                  { value: '', label: 'Not assigned' },
                  ...salesConsultants.map((consultant) => ({ value: consultant.id, label: consultant.name })),
                  { value: MANUAL_CONSULTANT_VALUE, label: 'Enter name manually…' },
                ]}
              />
              {activeDraft.isManualSalesConsultant && (
                <input
                  className="built-manual-consultant-input"
                  type="text"
                  value={activeDraft.salesConsultantManualName}
                  onChange={(event) => updateDraftField('salesConsultantManualName', event.target.value)}
                  placeholder="Sales consultant name"
                  autoFocus
                />
              )}
              {consultantLoadError && <span className="stock-admin-field-error">{consultantLoadError}</span>}
            </div>

            <div className="stock-admin-field">
              <span>Build Style</span>
              <BuiltAdminDropdown
                ariaLabel="Build Style"
                value={activeDraft.buildStyle}
                onChange={(value) => updateDraftField('buildStyle', value)}
                placeholder="Select build style"
                options={BUILD_STYLES.map((buildStyle) => ({ value: buildStyle, label: buildStyle }))}
              />
            </div>

            <label className="stock-admin-field">
              <span>Year Built</span>
              <input type="number" min="1800" max={new Date().getFullYear()} step="1" value={activeDraft.completedYear} onChange={(event) => updateDraftField('completedYear', event.target.value)} />
            </label>

            <div className="stock-admin-field">
              <span>Type</span>
              <BuiltAdminDropdown
                ariaLabel="Type"
                value={activeDraft.type}
                onChange={(value) => updateDraftField('type', value)}
                placeholder="Not established"
                options={[{ value: '', label: 'Not established' }, ...BUILT_HOME_TYPES.map((type) => ({ value: type, label: type }))]}
              />
            </div>

            <label className="stock-admin-field">
              <span>Council Consented Date</span>
              <input type="date" value={activeDraft.consentedDate} onChange={(event) => updateDraftField('consentedDate', event.target.value)} />
            </label>

            <div className="stock-admin-field">
              <span>Photo</span>
              {activeDraft.photoName ? (
                <div className="stock-admin-file-control">
                  <span className="stock-admin-file-name" title={activeDraft.photoName}>{activeDraft.photoName}</span>
                  <div className="stock-admin-file-actions">
                    <button
                      type="button"
                      className="stock-admin-icon-button stock-admin-trash-button"
                      onClick={() => setActiveDraft((current) => ({ ...current, photoName: '', photoUploadFile: null, removePhoto: true }))}
                      aria-label="Remove photo"
                      title="Remove photo"
                    >
                      <img src={TrashIcon} alt="" className="stock-admin-trash-icon" />
                    </button>
                  </div>
                </div>
              ) : (
                <label className="stock-admin-file-control stock-admin-file-control-empty">
                  <span className="stock-admin-file-upload-label">{isSanitizingPhoto ? 'Preparing…' : 'Upload'}</span>
                  <div className="stock-admin-file-actions">
                    <span className="stock-admin-icon-button" aria-label="Upload photo" title="Upload photo">
                      <input
                        type="file"
                        accept="image/*"
                        className="stock-admin-file-input"
                        disabled={isSanitizingPhoto}
                        onChange={async (event) => {
                          const file = event.target.files?.[0];
                          event.target.value = '';

                          if (!file) return;

                          setIsSanitizingPhoto(true);
                          setSaveError('');
                          try {
                            const sanitizedFile = await sanitizeImageFile(file);
                            setActiveDraft((current) => ({
                              ...current,
                              photoName: sanitizedFile.name,
                              photoUploadFile: sanitizedFile,
                              removePhoto: false,
                            }));
                          } catch (error) {
                            setSaveError(error?.message || 'The photo could not be prepared for upload.');
                          } finally {
                            setIsSanitizingPhoto(false);
                          }
                        }}
                      />
                      <UploadIcon />
                    </span>
                  </div>
                </label>
              )}
            </div>

          </div>

          <div className="stock-admin-footer">
            <div className="stock-admin-footer-status">{saveError && <div className="stock-admin-error">{saveError}</div>}</div>
            <div className="stock-admin-footer-actions">
              <button type="button" className="stock-admin-secondary-button" onClick={closeEditor} disabled={isSaving || isSanitizingPhoto}>Cancel</button>
              <button type="button" className="stock-admin-primary-button" onClick={handleSave} disabled={isSaving || isSanitizingPhoto}>
                {isSaving ? 'Saving...' : isSanitizingPhoto ? 'Preparing Photo...' : 'Save Home'}
              </button>
            </div>
          </div>
        </div>
      )}
      <BuiltCoordinatePicker
        isOpen={isCoordinatePickerOpen}
        address={activeDraft?.address || ''}
        initialCoordinates={(() => {
          try { return parseCoordinates(activeDraft?.coordinates) || null; }
          catch (error) { return null; }
        })()}
        onCancel={() => setIsCoordinatePickerOpen(false)}
        onApply={(position) => {
          updateDraftField('coordinates', formatCoordinates(position.lat, position.lng));
          setIsCoordinatePickerOpen(false);
        }}
      />
    </div>
  );
}
